import React, { useEffect } from "react";
import { isEmpty } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { change, Field, formValueSelector } from "redux-form";

import { FormControlLabel, GenericSwitch } from "components";
import { LineDivider, StripedRowsWithCols } from "components/layout";

import {
	cardProcessingFees,
	cardNetworkTokenizationFees,
	enhancesPlatformFees,
	feesHeader2,
	feesHeader3,
	platformFees,
} from "./ariseFeesFieldsMapping";
import FeesAriseField from "./feesAriseField";
import InfoSection from "./infoSection";
import FeeBlockContext from "../feeBlockContext";

import styles from "./feesArise.module.scss";

const FeesAriseFields = ({
	isEntryApplication = false,
	setWebsiteUpdatedValue,
	businessFormName = "businessForm",
	isMerchantFeesPage,
	formName,
	formValues,
	initialValues,
}: {
	isEntryApplication?: boolean;
	setWebsiteUpdatedValue: Function;
	businessFormName: string;
	isMerchantFeesPage?: boolean;
	formName: string;
	formValues: { [key: string]: any };
	initialValues?: { [key: string]: any };
}) => {
	const dispatch = useDispatch();

	const getFeesTable = (feesHeader, feesBody) => {
		const body = feesBody.map(fee => ({
			row: [
				{
					value: fee.setup ? (
						<FeesAriseField fee={fee.setup} />
					) : null,
				},
				{
					value: fee.monthly ? (
						<FeesAriseField fee={fee.monthly} />
					) : null,
				},
				{
					value: fee.perItem ? (
						<FeesAriseField fee={fee.perItem} />
					) : null,
				},
			],
		}));

		const result = [
			{
				...feesHeader,
			},

			...body,
		];

		return result;
	};

	const isCardNetworkTokenization = formValues?.isCardNetworkTokenization;
	const isCardNetworkFeesAvailable =
		initialValues?.isCardNetworkFeesAvailable;

	const selector = formValueSelector(businessFormName);
	const getWebsiteValue = state => selector(state, "website");
	const websiteValue = useSelector(getWebsiteValue);
	const isWebsiteWarning = isEmpty(websiteValue);

	useEffect(() => {
		if (
			+initialValues?.cardNetworkPerTokenizationEventFee > 0 ||
			+initialValues?.cardNetworkTokenizationMonthlyFee > 0 ||
			+initialValues?.cardNetworkTokenizationSetupFee > 0
		) {
			dispatch(change(formName, "isCardNetworkTokenization", true));
		}
	}, [
		dispatch,
		formName,
		initialValues?.cardNetworkPerTokenizationEventFee,
		initialValues?.cardNetworkTokenizationMonthlyFee,
		initialValues?.cardNetworkTokenizationSetupFee,
	]);

	return (
		<FeeBlockContext.Provider value="AriseFees">
			{!isEntryApplication && (
				<h4 className={`${styles.header} pl-4 mb-4`}>ARISE Fees</h4>
			)}
			<h4 className={`${styles.header} pl-4`}>Platform Fee</h4>
			<StripedRowsWithCols
				colsQty={3}
				className={styles.feesContainer}
				stripedRowClassName={styles.feesRow}
				items={getFeesTable?.(feesHeader2, platformFees)}
				isLoading={false}
				bgReverse
			/>
			<LineDivider />
			<h4 className={`${styles.header} pl-4`}>
				Enhances Platform Features
			</h4>
			<StripedRowsWithCols
				colsQty={3}
				className={styles.feesContainer}
				stripedRowClassName={styles.feesRow}
				items={getFeesTable?.(feesHeader3, enhancesPlatformFees)}
				isLoading={false}
				bgReverse
			/>
			<LineDivider />
			<h4 className={`${styles.header} pl-4`}>Card processing</h4>
			<StripedRowsWithCols
				colsQty={3}
				className={styles.feesContainer}
				stripedRowClassName={styles.feesRow}
				items={getFeesTable?.(
					feesHeader3,
					isCardNetworkFeesAvailable
						? [
								...cardProcessingFees,
								...cardNetworkTokenizationFees,
						  ]
						: cardProcessingFees,
				)}
				isLoading={false}
				bgReverse
			/>
			{!isMerchantFeesPage && (
				<>
					<LineDivider />
					<FormControlLabel
						control={
							<Field
								component={GenericSwitch}
								name={"isCardNetworkTokenization"}
								id={"isCardNetworkTokenization"}
								type="checkbox"
							/>
						}
						label={"Card Network Tokenization"}
						className={styles.toggle}
					/>
				</>
			)}

			{isCardNetworkTokenization && (
				<>
					<StripedRowsWithCols
						colsQty={3}
						className={styles.feesContainer}
						stripedRowClassName={styles.feesRow}
						items={getFeesTable?.(
							feesHeader3,
							cardNetworkTokenizationFees,
						)}
						isLoading={false}
						bgReverse
					/>
					{isWebsiteWarning && (
						<InfoSection
							setWebsiteUpdatedValue={setWebsiteUpdatedValue}
							businessFormName={businessFormName}
						/>
					)}
				</>
			)}
			<LineDivider />
		</FeeBlockContext.Provider>
	);
};

export default FeesAriseFields;
